import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '../modules/auth/store'

// Páginas de autenticación
import { LoginPage } from '../modules/auth/pages/LoginPage'
import { VerifyPage } from '../modules/auth/pages/VerifyPage'

// Páginas protegidas
import { DashboardPage } from '../modules/dashboard/pages/DashboardPage'
import { ProfilePage } from '../pages/ProfilePage'

// Mascotas
import { PetsListPage } from '../modules/pets/pages/PetsListPage'
import { PetDetailPage } from '../modules/pets/pages/PetDetailPage'
import { PetFormPage } from '../modules/pets/pages/PetFormPage'

// Medicamentos
import { MedicationFormPage } from '../modules/medications/pages/MedicationFormPage'

// Controles
import { CheckupFormPage } from '../modules/checkups/pages/CheckupFormPage'

// Vacunas
import { VaccineFormPage } from '../modules/vaccines/pages/VaccineFormPage'

// Grupo
import { GroupPage } from '../modules/group/pages/GroupPage'
import { InvitationPage } from '../modules/group/pages/InvitationPage'

// Bitácora
import { BitacoraPage } from '../modules/audit/pages/BitacoraPage'

// Layout
import { Layout } from '../shared/components/Layout'

// Componente para rutas protegidas
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />
  }

  return <>{children}</>
}

// Componente para rutas protegidas con Layout
function ProtectedRouteWithLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />
  }

  return <Layout>{children}</Layout>
}

// Componente para rutas públicas (redirigir si ya está autenticado)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export function AppRouter() {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/auth/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Verify no usa PublicRoute porque maneja su propio estado */}
      <Route path="/auth/verify" element={<VerifyPage />} />

      {/* Invitación - puede ser accedida sin login */}
      <Route path="/invitacion" element={<InvitationPage />} />

      {/* Rutas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Mascotas */}
      <Route
        path="/mascotas"
        element={
          <ProtectedRouteWithLayout>
            <PetsListPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/nueva"
        element={
          <ProtectedRouteWithLayout>
            <PetFormPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/:id"
        element={
          <ProtectedRouteWithLayout>
            <PetDetailPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/:id/editar"
        element={
          <ProtectedRouteWithLayout>
            <PetFormPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Medicamentos */}
      <Route
        path="/mascotas/:petId/medicamentos/nuevo"
        element={
          <ProtectedRouteWithLayout>
            <MedicationFormPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/:petId/medicamentos/:id/editar"
        element={
          <ProtectedRouteWithLayout>
            <MedicationFormPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Controles veterinarios */}
      <Route
        path="/mascotas/:petId/controles/nuevo"
        element={
          <ProtectedRouteWithLayout>
            <CheckupFormPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/:petId/controles/:id/editar"
        element={
          <ProtectedRouteWithLayout>
            <CheckupFormPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Vacunas */}
      <Route
        path="/mascotas/:petId/vacunas/nueva"
        element={
          <ProtectedRouteWithLayout>
            <VaccineFormPage />
          </ProtectedRouteWithLayout>
        }
      />
      <Route
        path="/mascotas/:petId/vacunas/:id/editar"
        element={
          <ProtectedRouteWithLayout>
            <VaccineFormPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Grupo */}
      <Route
        path="/grupo"
        element={
          <ProtectedRouteWithLayout>
            <GroupPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Bitácora de Cuidado (admin only) */}
      <Route
        path="/bitacora"
        element={
          <ProtectedRouteWithLayout>
            <BitacoraPage />
          </ProtectedRouteWithLayout>
        }
      />

      {/* Perfil */}
      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
